<template>
    <div class="stake-lst">
        <!-- Direction Toggle -->
        <div class="d-flex align-items-center gap-2 mb-3">
            <div class="flex-fill">
                <label class="form-label small fw-bold mb-1">
                    {{ input.symbol }}
                    <img :src="input.icon" :alt="input.symbol" style="width:20px;height:20px;border-radius:50%;margin-left:4px">
                </label>
                <div class="input-group">
                    <input
                        type="number"
                        class="form-control font-mono-num"
                        v-model.number="inAmount"
                        step="0.01"
                        min="0"
                        placeholder="0.00"
                        @input="onInputChange"
                    >
                    <button
                        class="btn btn-outline-secondary"
                        @click="setMax"
                        :disabled="!walletConnected"
                    >MAX</button>
                </div>
                <div v-if="walletConnected" class="small text-muted mt-1">
                    Balance: <span class="font-mono-num">{{ formatBalance(input.address) }}</span> {{ input.symbol }}
                </div>
            </div>
        </div>

        <!-- Swap Direction Button -->
        <div class="text-center mb-3">
            <button class="compass-btn compass-btn--outline compass-btn--sm" @click="swapDirection">
                &#8693; Swap Direction
            </button>
        </div>

        <!-- Output -->
        <div class="mb-3">
            <label class="form-label small fw-bold mb-1">
                {{ output.symbol }}
                <img :src="output.icon" :alt="output.symbol" style="width:20px;height:20px;border-radius:50%;margin-left:4px">
            </label>
            <input
                type="text"
                class="form-control font-mono-num"
                :value="outAmountDisplay"
                disabled
            >
            <div v-if="walletConnected" class="small text-muted mt-1">
                Balance: <span class="font-mono-num">{{ formatBalance(output.address) }}</span> {{ output.symbol }}
            </div>
        </div>

        <!-- Rate Info -->
        <div v-if="input.symbol === 'SOL'" class="compass-info-box mb-3" style="padding: 0.75rem;">
            <div class="d-flex justify-content-between small">
                <span>Pool Ratio</span>
                <span class="font-mono-num">1 {{ input.symbol }} = {{ ratio.toFixed(6) }} {{ output.symbol }}</span>
            </div>
        </div>
        <div v-else-if="quoteResponse?.routePlan" class="compass-info-box mb-3" style="padding: 0.75rem;">
            <div class="d-flex justify-content-between small mb-1">
                <span>Jupiter Route</span>
                <span class="font-mono-num">
                    <template v-for="(route, i) in quoteResponse.routePlan" :key="i">
                        {{ route.swapInfo?.label }}<template v-if="i < quoteResponse.routePlan.length - 1"> → </template>
                    </template>
                </span>
            </div>
            <div class="d-flex justify-content-between small text-muted">
                <span>Slippage</span>
                <span class="font-mono-num">{{ ((quoteResponse?.slippageBps || 0) / 100).toFixed(2) }}%</span>
            </div>
            <div v-if="quoteResponse?.priceImpactPct" class="d-flex justify-content-between small text-muted">
                <span>Price Impact</span>
                <span class="font-mono-num">{{ (Number(quoteResponse.priceImpactPct) * 100).toFixed(2) }}%</span>
            </div>
        </div>

        <!-- Region restriction -->
        <div v-if="isRestricted" class="alert alert-warning small mb-3">
            Staking is not available in your region.
        </div>

        <!-- Action Button -->
        <button
            class="compass-btn compass-btn--primary w-100"
            :disabled="!canSwap || swapping"
            @click="swap"
        >
            <span v-if="swapping" class="spinner-border spinner-border-sm me-2"></span>
            <template v-if="!walletConnected">Connect Wallet</template>
            <template v-else-if="swapping">Processing...</template>
            <template v-else-if="input.symbol === 'SOL'">Stake SOL → compassSOL</template>
            <template v-else>Unstake compassSOL → SOL</template>
        </button>

        <!-- Result -->
        <div v-if="txResult" class="alert alert-success mt-3 small">
            {{ txResult.message }}
            <a v-if="txResult.explorerUrl" :href="txResult.explorerUrl" target="_blank">View transaction →</a>
        </div>
        <div v-if="error" class="alert alert-danger mt-3 small">{{ error }}</div>
    </div>
</template>

<script>
import { defineComponent } from 'vue';
import { useWallet } from 'solana-wallets-vue';
import { getAssetsByOwner, getSolBalance } from '../utils/balances';
import { ElNotification } from 'element-plus';
import { Connection, Transaction, PublicKey, VersionedTransaction, ComputeBudgetProgram } from '@solana/web3.js';
import { depositSolIntoSanctum } from '../utils/sanctum';

const COMPASSSOL_MINT = 'Comp4ssDzXcLeu2MnLuGNNFC4cmLPMng8qWHPvzAMU1h';
const SOL_MINT = 'So11111111111111111111111111111111111111112';
const SANCTUM_POOL = 'AwDeTcW6BovNYR34Df1TPm4bFwswa4CJY4YPye2LXtPS';

function getRpcEndpoint() {
    return window.swapConfig?.rpcEndpoint || 'https://api.mainnet-beta.solana.com';
}

export default defineComponent({
    name: 'StakeLst',
    data() {
        return {
            input: null,
            output: null,
            inAmount: 0.1,
            wallet: useWallet(),
            tokens: [
                {
                    symbol: 'SOL',
                    icon: 'https://raw.githubusercontent.com/solana-labs/token-list/main/assets/mainnet/So11111111111111111111111111111111111111112/logo.png',
                    address: SOL_MINT,
                },
                {
                    symbol: 'compassSOL',
                    icon: '/compass.png',
                    address: COMPASSSOL_MINT,
                },
            ],
            ratio: 1.01,
            pool: null,
            balances: {},
            quoteResponse: null,
            swapping: false,
            txResult: null,
            error: null,
        };
    },
    computed: {
        walletConnected() {
            return !!this.wallet?.connected;
        },
        isRestricted() {
            return window.geo === 'GB';
        },
        canSwap() {
            return this.walletConnected && this.inAmount > 0 && !this.isRestricted && !this.swapping;
        },
        outAmountDisplay() {
            if (!this.input || !this.output) return '0.000000000';
            if (this.input.symbol === 'SOL') {
                return (this.inAmount * this.ratio).toFixed(9);
            }
            return ((this.quoteResponse?.outAmount || 0) / 1e9).toFixed(9);
        },
    },
    mounted() {
        this.input = this.tokens[0];
        this.output = this.tokens[1];
        this.loadPoolData();
        if (this.walletConnected) this.getBalances();
        // Delay balance + quote fetch to allow wallet connection
        setTimeout(() => {
            this.getBalances();
            this.getSolQuote();
        }, 5000);
    },
    watch: {
        'wallet.connected': {
            immediate: true,
            handler(val) {
                if (val) this.getBalances();
            },
        },
    },
    methods: {
        formatBalance(address) {
            const bal = this.balances[address] || 0;
            return bal.toFixed(6);
        },

        setMax() {
            const bal = this.balances[this.input?.address] || 0;
            // Leave 0.01 SOL for fees when staking SOL
            this.inAmount = this.input?.symbol === 'SOL' ? Math.max(0, bal - 0.01) : bal;
            this.onInputChange();
        },

        onInputChange() {
            if (this.output?.symbol === 'SOL') {
                this.getSolQuote();
            }
        },

        async loadPoolData() {
            try {
                const response = await axios.get('/stake-pools', {
                    headers: { 'X-Requested-With': 'XMLHttpRequest' },
                });
                const stakePools = response.data[0];
                this.pool = stakePools.filter(pool => pool.poolMint === COMPASSSOL_MINT)[0];
                if (this.pool) {
                    // Ratio: how many compassSOL per SOL
                    // poolTokenSupply / totalLamports * 0.999 (0.1% Sanctum fee)
                    this.ratio = (this.pool.poolTokenSupply / this.pool.totalLamports) * 0.999;
                }
            } catch (e) {
                console.error('[StakeLST] Failed to load pool data:', e);
            }
        },

        swapDirection() {
            const temp = this.input;
            this.input = this.output;
            this.output = temp;
            if (this.pool) {
                this.ratio = (this.pool.poolTokenSupply / this.pool.totalLamports) * 0.999;
                if (this.input.symbol !== 'SOL') {
                    // Reverse ratio for compassSOL → SOL
                    this.ratio = (this.pool.totalLamports / this.pool.poolTokenSupply);
                }
            }
            this.quoteResponse = null;
            this.getSolQuote();
        },

        async swap() {
            if (!this.canSwap) return;
            this.swapping = true;
            this.error = null;
            this.txResult = null;

            try {
                if (this.output.address === COMPASSSOL_MINT) {
                    await this.stake();
                } else {
                    await this.unstake();
                }
            } catch (e) {
                this.error = e.message || 'Transaction failed';
            } finally {
                this.swapping = false;
            }
        },

        async stake() {
            const lamports = Math.floor(this.inAmount * 1e9);
            const connection = new Connection(getRpcEndpoint());

            const depositTx = await depositSolIntoSanctum(
                connection,
                new PublicKey(SANCTUM_POOL),
                this.wallet.publicKey,
                lamports,
                undefined,
                undefined,
                undefined,
            );

            const transaction = new Transaction();
            transaction.add(
                ComputeBudgetProgram.setComputeUnitPrice({ microLamports: 100 }),
            );
            transaction.add(...depositTx.instructions);

            const hash = await connection.getLatestBlockhash();
            transaction.recentBlockhash = hash.blockhash;
            transaction.feePayer = this.wallet.publicKey;

            const signers = depositTx.signers;
            if (signers.length > 0) {
                transaction.sign(...signers);
            }

            const txid = await this.wallet.sendTransaction(transaction, connection, {
                skipPreflight: true,
                maxRetries: 2,
            });

            this.txResult = {
                message: `Staked ${this.inAmount} SOL → compassSOL!`,
                explorerUrl: `https://solscan.io/tx/${txid}`,
            };

            ElNotification({
                title: 'Success',
                message: `Staked ${this.inAmount} SOL for compassSOL`,
                type: 'success',
                duration: 5000,
            });

            // Log for analytics
            this.logStake(txid);
            this.getBalances();
        },

        async getSolQuote() {
            if (!this.output || this.output.address !== SOL_MINT) return;
            if (!this.inAmount || this.inAmount <= 0) return;
            try {
                const resp = await fetch(
                    `https://quote-api.jup.ag/v6/quote?inputMint=${this.input.address}&outputMint=${SOL_MINT}&amount=${Math.floor(this.inAmount * 1e9)}&slippageBps=50`,
                );
                if (resp.ok) {
                    this.quoteResponse = await resp.json();
                }
            } catch (e) {
                console.error('[StakeLST] Quote failed:', e);
            }
        },

        async unstake() {
            const quoteResponse = await this.getSolQuote();
            if (!this.quoteResponse) throw new Error('Could not get swap quote');

            const { swapTransaction } = await (
                await fetch('https://quote-api.jup.ag/v6/swap', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        quoteResponse: this.quoteResponse,
                        userPublicKey: this.wallet.publicKey.toString(),
                        wrapAndUnwrapSol: true,
                        prioritizationFeeLamports: 'auto',
                        autoMultiplier: 2,
                        dynamicComputeUnitLimit: true,
                    }),
                })
            ).json();

            if (!swapTransaction) throw new Error('Failed to build swap transaction');

            const txBuf = Buffer.from(swapTransaction, 'base64');
            const transaction = VersionedTransaction.deserialize(txBuf);
            const connection = new Connection(getRpcEndpoint());

            const txid = await this.wallet.sendTransaction(transaction, connection, {
                skipPreflight: true,
                maxRetries: 2,
            });

            const solOut = ((this.quoteResponse.outAmount || 0) / 1e9).toFixed(4);
            this.txResult = {
                message: `Swapped compassSOL → ${solOut} SOL!`,
                explorerUrl: `https://solscan.io/tx/${txid}`,
            };

            ElNotification({
                title: 'Success',
                message: `Unstaked compassSOL → ${solOut} SOL`,
                type: 'success',
                duration: 5000,
            });

            this.getBalances();
        },

        async getBalances() {
            if (!this.walletConnected) return;
            try {
                const balances = await getAssetsByOwner(this.wallet.publicKey);
                const mapped = {};
                for (const item of balances) {
                    if (item?.token_info && item?.id) {
                        mapped[item.id] = (item.token_info.balance / 1e9) || 0;
                    }
                }
                mapped[SOL_MINT] = (await getSolBalance(this.wallet.publicKey)) / 1e9;
                this.balances = mapped;
            } catch (e) {
                console.error('[StakeLST] Failed to load balances:', e);
            }
        },

        logStake(txid) {
            try {
                if (typeof clicky === 'object') {
                    clicky.log('/stake/compassSOL/' + this.inAmount + '/' + txid);
                    clicky.goal('Swapped to compassSOL', this.inAmount);
                }
                axios.post('/log/stake', {
                    txid,
                    staker: this.wallet.publicKey?.toString(),
                    amount: this.inAmount,
                    wallet: this.wallet.wallet?.adapter?.name,
                    stake_to: 'compassSOL',
                });
            } catch (e) {
                // Analytics logging is non-critical
            }
        },
    },
});
</script>
